import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  Offer,
  OFFER_DURATION_1M,
  OFFER_DURATION_1Y,
  OFFER_DURATION_3M,
  OFFER_DURATION_6M,
  SOURCE_VIDAPP,
  SupportedOfferDuration,
} from "@/api";
import { Button } from "@/components";
import { useAppProperties } from "@/hooks";
import { useTranslate, useUser } from "@/providers/context";
import { cn, getOfferFrequency } from "@/utils";

import { useStripeCheckout } from "../hooks/useStripeCheckout";
import { useGrantFreeOffer } from "../hooks/useGrantFreeOffer";
import { OfferDescription } from "./OfferDescription";

const getDurationInDays = (duration: SupportedOfferDuration) => {
  switch (duration) {
    case OFFER_DURATION_1M:
      return 365 / 12;
    case OFFER_DURATION_3M:
      return (365 / 12) * 3;
    case OFFER_DURATION_6M:
      return (365 / 12) * 6;
    case OFFER_DURATION_1Y:
    default:
      return 365;
  }
};

const getPricePerDay = (price: number, duration: SupportedOfferDuration) => {
  const days = getDurationInDays(duration);
  return price / days;
};

export const OfferCard = ({
  offer,
  lowestDuration,
  comparativePrice,
  comparativeDuration,
}: {
  offer: Offer;
  lowestDuration: SupportedOfferDuration;
  comparativePrice: number | undefined;
  comparativeDuration: string | undefined;
}) => {
  const {
    FinalDisplayName,
    WebPriceUSD,
    SubscriptionDuration,
    Description,
    TrialDisplay,
    DataSource,
    ProductIdWeb,
    SourceProductId,
  } = offer;

  const trial =
    TrialDisplay && TrialDisplay !== "No Trial" ? TrialDisplay : undefined;

  const stripeCheckout = useStripeCheckout();
  const grantFreeOffer = useGrantFreeOffer();
  const { detailed_products, platform } = useUser();
  const { data } = useAppProperties();
  const { translate } = useTranslate();
  const navigate = useNavigate();

  const isFreeOffer = WebPriceUSD === 0 && DataSource === SOURCE_VIDAPP;

  const isLoading = stripeCheckout.isLoading || grantFreeOffer.isLoading;

  const isDisabled = useMemo(() => {
    if (!detailed_products || isLoading) {
      return true;
    }
    // A user with no products can choose all options
    if (detailed_products.length === 0) {
      return false;
    }

    /*
      User has a subscription product
     */
    if (isFreeOffer) {
      // Free products only available for users with no subscription
      return true;
    }
    // A user with a free granted product can upgrade to paid
    return platform !== "Manual";
  }, [detailed_products, isFreeOffer, platform, isLoading]);

  const isSubscribed = useMemo(
    () => detailed_products?.some((p) => p.product_id === ProductIdWeb),
    [ProductIdWeb, detailed_products],
  );

  const equivalentPricing = useMemo(() => {
    if (SubscriptionDuration === lowestDuration) {
      return WebPriceUSD;
    }

    const pricePerDay = getPricePerDay(WebPriceUSD, SubscriptionDuration);
    const lowestDurationInDays = getDurationInDays(lowestDuration);
    const price = pricePerDay * lowestDurationInDays;
    if (data?.WebDefaultCurrency === "JPY") {
      return Math.ceil(price);
    } else {
      return price.toFixed(2);
    }
  }, [
    WebPriceUSD,
    SubscriptionDuration,
    lowestDuration,
    data?.WebDefaultCurrency,
  ]);

  const savePercent = useMemo(() => {
    if (
      data?.ShowOfferSalePercentage !== "1" ||
      !comparativePrice ||
      !comparativeDuration ||
      WebPriceUSD === 0 ||
      WebPriceUSD === comparativePrice
    ) {
      return undefined;
    }

    const pricePerDay = getPricePerDay(WebPriceUSD, SubscriptionDuration);
    const comparativePricePerDay = getPricePerDay(
      comparativePrice,
      comparativeDuration,
    );

    const difference = comparativePricePerDay - pricePerDay;
    if (difference <= 0) {
      return undefined;
    }

    const roundedPercentage = Math.round(
      (difference / comparativePricePerDay) * 100,
    );
    return roundedPercentage > 0 ? roundedPercentage : undefined;
  }, [
    data?.ShowOfferSalePercentage,
    SubscriptionDuration,
    WebPriceUSD,
    comparativePrice,
    comparativeDuration,
  ]);

  const proceedWithOffer = useCallback(() => {
    // Call User API stripe checkout setup
    // Redirect to Stripe
    stripeCheckout.mutate(
      {
        priceID: ProductIdWeb ?? "",
      },
      {
        onSuccess: ({ url }) => {
          window.location.href = url;
        },
      },
    );
  }, [stripeCheckout, ProductIdWeb]);

  const proceedWithFreeOffer = useCallback(() => {
    grantFreeOffer.mutate(
      {
        sourceProductId: SourceProductId,
      },
      {
        onSuccess: () => {
          navigate("/purchase-success", { replace: true });
        },
      },
    );
  }, [grantFreeOffer, SourceProductId, navigate]);

  const currencySymbol = data?.WebDefaultCurrencySymbol || "$";

  const ButtonText = useMemo(() => {
    if (isSubscribed) {
      return translate("Current Plan");
    }
    if (isFreeOffer) {
      return translate("Get Access");
    }
    if (trial) {
      return `${translate("Start")} ${translate(trial)} `;
    }
    return translate("Select Plan");
  }, [isSubscribed, isFreeOffer, trial, translate]);

  return (
    <div className="rounded-[12px] border border-border p-5">
      <div className="flex w-full justify-between">
        <div className="p1-semibold mb-1">{FinalDisplayName}</div>
        {savePercent && (
          <div className="caption h-fit shrink-0 rounded-md bg-highlightBg px-[7px] py-1 uppercase text-highlightPrimary">{`${translate("Save ~Money~")} ${savePercent}%`}</div>
        )}
      </div>
      <OfferDescription Description={Description} />
      {isFreeOffer ? (
        <div className="h1 mb-4">{translate("Free")}</div>
      ) : (
        <div className="p1-semibold mb-4 flex w-full flex-wrap items-end justify-between gap-1">
          <div className="pt-0.5">
            <span className="h1 leading-6">
              {currencySymbol}
              {equivalentPricing}
            </span>
            <span className="p1 text-item-secondary">
              /{getOfferFrequency(lowestDuration, translate)}
            </span>
          </div>
          <div className="caption pb-[1px] text-item-secondary">
            {currencySymbol}
            {WebPriceUSD} {translate("billed")}{" "}
            {getOfferFrequency(SubscriptionDuration, translate, true)}
          </div>
        </div>
      )}
      <Button
        size="m"
        className={cn(
          "h-fit w-full px-[14px] py-[10px]",
          isSubscribed && !isDisabled && "bg-highlightSecondary",
        )}
        disabled={isDisabled}
        loading={isLoading}
        onClick={isFreeOffer ? proceedWithFreeOffer : proceedWithOffer}
      >
        {ButtonText}
      </Button>
    </div>
  );
};
